import { computed, toValue, type MaybeRefOrGetter, type Ref, type ComputedRef } from 'vue';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import axios from 'axios';
import http from '@/services/http';
import type { IScope } from '@/types/filterBuilderTypes';
import { filterNullValues } from '@/utils/filterUtils';

/**
 * Severity slugs persisted by the Laravel verifier
 * (see `App\Enums\AudioFileIssueSeverity`).
 */
export type AudioFileIssueSeverity = 'error' | 'warning' | 'info';

/**
 * Canonical issue-type slugs, in `App\Enums\AudioFileIssueType::cases()` order.
 * Extend together with `AUDIO_FILE_ISSUE_TYPE_OPTIONS` in
 * `@/constants/audioFileIssueEnums`, whose test pins both to the PHP enum.
 */
export type AudioFileIssueType =
    | 'gvlibrary_missing'
    | 'gvlibrary_size_mismatch'
    | 'gvlibrary_unreadable'
    | 'original_missing'
    | 'original_unreadable'
    | 'vbr_detected'
    | 'bitrate_mismatch'
    | 'low_bitrate'
    | 'hls_master_missing'
    | 'hls_variant_missing'
    | 'hls_chunk_count_mismatch'
    | 'hls_master_unplayable'
    | 'hls_unreadable'
    | 'mp3val_error'
    | 'mp3val_warning'
    | 'mp3val_not_run'
    | 'normalization_metadata_missing';

export type OriginalBitrateInspectionStatus = 'missing' | 'invalid' | 'valid' | null;

export type AudioFileIssueRow = {
    id: number;
    live_track_id: number;
    live_track_code: string;
    live_track_name: string | null;
    live_track_artist_id: number | null;
    live_track_artist_name?: string | null;
    cover_art_signed_url?: string | null;
    issue_type: AudioFileIssueType;
    issue_type_label: string;
    severity: AudioFileIssueSeverity;
    severity_label: string;
    details: Record<string, unknown> | null;
    verified_at: string | null;
    /** When this issue row was persisted (verification run writes). */
    created_at: string | null;
    /** Live track MP3/library file last update (`tracks.file_updated_at`). */
    uploaded_date?: string | null;
    /** When the track file was originally uploaded (`tracks.uploaded_date`). */
    track_uploaded_date?: string | null;
    /** When the live track row was created (`tracks.created_at`). List endpoint only. */
    track_created_at?: string | null;
    /**
     * Per-track joined fields populated by the list endpoint (POST /search and
     * the legacy GET) so the column manager can surface artist / album / etc.
     * without an extra request. Only set on list responses; show / rerun
     * payloads omit them.
     */
    album_name?: string | null;
    genre_name?: string | null;
    record_label_name?: string | null;
    isrc?: string | null;
    copyright?: number | null;
    bitrate?: number | null;
    duration?: number | null;
    /** `tracks.playlist_count` — number of buckets/custom lists using the live track (0 = unused). List endpoint only. */
    playlist_count?: number | null;
    remix_name?: string | null;
    original_status?: string | null;
    original_bitrate_kbps?: number | null;
    gvlibrary_bitrate_kbps?: number | null;
    /** `tracks.rating` — list endpoint only (joined in ranked subquery). */
    rating?: string | null;
    /**
     * List-only aggregate: total number of matching issues for this track
     * within the filtered set. Omitted from per-track responses (show / rerun)
     * where each row already represents an individual issue.
     */
    issues_count?: number;
    /** List-only aggregate: distinct issue-type slugs for this track in the filtered set. */
    issue_types?: AudioFileIssueType[];
};

export type AudioFileIssuesListResponse = {
    data: AudioFileIssueRow[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        /** ISO timestamp when the response is scoped to a single verification run, otherwise `null`. */
        verified_at: string | null;
    };
};

export type AudioFileIssuesByTrackResponse = {
    track: {
        id: number;
        code: string;
        name: string | null;
        remix_name: string | null;
        isrc: string | null;
        artist_name?: string | null;
        album_name?: string | null;
        genre_name?: string | null;
        copyright?: number | null;
        bitrate: number | null;
        duration: number | null;
        original_duration: number | null;
        file_updated_at: string | null;
        /** Same as `file_updated_at` — explicit label for UI. */
        uploaded_date?: string | null;
        /** Original track upload timestamp (`tracks.uploaded_date`). */
        track_uploaded_date?: string | null;
        created_at?: string | null;
        cover_art_signed_url?: string | null;
        raw_ddex_track_id?: number | null;
        rating?: string | null;
        /** Count of places the live track is in use (0 = unused). */
        playlist_count?: number | null;
    } | null;
    original_bitrate: {
        status: OriginalBitrateInspectionStatus;
        verified_at: string | null;
        detected_bitrate_kbps: number | null;
        object_etag?: string | null;
        is_vbr?: boolean | null;
    } | null;
    original_backup_file?: {
        storage_key: string;
        variant: string;
        content_length_bytes: number;
        etag: string;
        /** When the backup file was written to S3 (its `LastModified`). */
        last_modified_at?: string | null;
    } | null;
    gvlibrary: {
        detected_bitrate_kbps: number | null;
    } | null;
    issues: AudioFileIssueRow[];
    target_bitrate_kbps: number;
};

export type AudioFileIssueDdexMatchRow = {
    live_track_match_id: number;
    raw_ddex_track_id: number;
    isrc_match: boolean;
    fuzzy_match: boolean;
    confidence_score: number | null;
    /** DDEX ERN message date from {@link RawDdexTrack}. */
    message_date: string | null;
    matched_track: Record<string, unknown>;
};

export type AudioFileIssuesListFilters = {
    severity?: AudioFileIssueSeverity | '';
    issueTypes?: string[];
    q?: string;
    verifiedAt?: string;
    page?: number;
    perPage?: number;
};

export type AudioPlaybackDescriptorStream = {
    kind: 'stream';
    url: string;
    mime: string;
    content_length: number | null;
};

export type AudioPlaybackDescriptorHls = {
    kind: 'hls';
    url: string;
    segment_seconds: number;
    mime: string;
};

export type AudioPlaybackDescriptorUrl = {
    kind: 'url';
    url: string;
    mime: string;
    content_length: number | null;
};

export type AudioPlaybackDescriptor =
    | AudioPlaybackDescriptorStream
    | AudioPlaybackDescriptorHls
    | AudioPlaybackDescriptorUrl;

export type IssueTypeOption = { value: string; label: string };

export const ROOT_KEY = ['audio-file-issues'] as const;

function normalizeApiOrigin(): string {
    return (import.meta.env.VITE_API_URL as string)?.replace(/\/$/, '') ?? '';
}

export function resolvePlaybackUrl(descriptorUrl: string): string {
    if (descriptorUrl.startsWith('http://') || descriptorUrl.startsWith('https://')) {
        return descriptorUrl;
    }
    if (descriptorUrl.startsWith('/')) {
        return `${normalizeApiOrigin()}${descriptorUrl}`;
    }

    return descriptorUrl;
}

export function useAudioFileIssuesList(filters: MaybeRefOrGetter<AudioFileIssuesListFilters>) {
    return useQuery({
        queryKey: computed(() => [...ROOT_KEY, 'list', toValue(filters)]),
        queryFn: () => {
            const value = toValue(filters);
            const params = new URLSearchParams();
            if (value.severity) {
                params.set('severity', value.severity);
            }
            if (value.issueTypes && value.issueTypes.length > 0) {
                params.set('issue_type', value.issueTypes.join(','));
            }
            if (value.q?.trim()) {
                params.set('q', value.q.trim());
            }
            if (value.verifiedAt) {
                params.set('verified_at', value.verifiedAt);
            }
            if (value.page) {
                params.set('page', String(value.page));
            }
            if (value.perPage) {
                params.set('per_page', String(value.perPage));
            }
            const qs = params.toString();
            const url = qs ? `/api/audio-file-issues?${qs}` : '/api/audio-file-issues';
            return http.get<AudioFileIssuesListResponse>(url).then((response) => response.data);
        },
    });
}

export type AudioFileIssueFilterSchemaResponse = {
    fields: Array<{
        key: string;
        header: string;
        dataType: 'string' | 'number' | 'Date';
        sortable: boolean;
        filterable: boolean;
        isNullable?: boolean;
        options?: Array<{ value: string; label: string }>;
    }>;
};

/**
 * Fetches the backend's schema definition for audio-file-issues filterable
 * fields. The list of fields is hand-maintained in
 * {@link file:tabasco/app/Services/AudioFileIssueFilterSchema.php}; the
 * frontend mirror lives in {@link file:src/composables/audioFileIssuesSchema.ts}
 * for the synchronous filter / column UI, and this composable is what feeds
 * any options metadata (severity enum values, issue-type enum values, etc.)
 * the UI needs to render multi-selects.
 */
export function useAudioFileIssuesFilterSchema() {
    return useQuery({
        queryKey: [...ROOT_KEY, 'filter-schema'],
        queryFn: () =>
            http
                .get<AudioFileIssueFilterSchemaResponse>('/api/audio-file-issues/filter-schema')
                .then((response) => response.data),
        staleTime: 60 * 60 * 1000,
    });
}

export interface AudioFileIssuesSearchParams {
    pageIndex: Ref<number> | ComputedRef<number>;
    pageSize: Ref<number> | ComputedRef<number>;
    sorting?: Ref<object> | ComputedRef<object>;
    filter?: Ref<object> | ComputedRef<object>;
    verifiedAt?: Ref<string | null> | ComputedRef<string | null>;
}

/**
 * Issues a POST to `/api/audio-file-issues/search` so the rebuilt page can
 * send the rich {@link IScope} filter + sort spec the search page also uses,
 * instead of the legacy query-string filters. Mirrors the shape of
 * {@link useSearchApi} (entitySlug aside, since this endpoint is
 * page-specific). Returns the same paginated response shape as the legacy
 * GET endpoint so the dedupe / `issues_count` contracts continue to apply.
 */
export function useAudioFileIssuesSearch({
    pageIndex,
    pageSize,
    sorting,
    filter,
    verifiedAt,
}: AudioFileIssuesSearchParams) {
    const queryKey = [...ROOT_KEY, 'search', pageIndex, pageSize, sorting, filter, verifiedAt] as const;
    const queryFn = () => {
        let cleanedFilter: Record<string, unknown> | undefined = undefined;
        const rawFilter = filter?.value;
        if (rawFilter && typeof rawFilter === 'object') {
            let ruleScopesPayload: IScope | undefined;
            if ('ruleScopes' in rawFilter && (rawFilter as { ruleScopes?: IScope }).ruleScopes) {
                const cleanedScope = filterNullValues(
                    (rawFilter as { ruleScopes: IScope }).ruleScopes,
                );
                if (cleanedScope) {
                    ruleScopesPayload = cleanedScope;
                }
            }
            if (ruleScopesPayload) {
                cleanedFilter = { ruleScopes: ruleScopesPayload };
            }
        }

        const size = pageSize?.value ?? 50;
        const index = pageIndex?.value ?? 0;
        const verifiedAtValue = verifiedAt?.value ?? null;

        return http
            .post<AudioFileIssuesListResponse>('/api/audio-file-issues/search', {
                perPage: size,
                page: Math.floor(index / size) + 1,
                sort: sorting?.value || undefined,
                filter: cleanedFilter,
                verified_at: verifiedAtValue ?? undefined,
            })
            .then((response) => response.data);
    };
    // `placeholderData: keepPreviousData` keeps the previous response visible
    // while a new page / sort / filter request is in flight. Without it, vue-query
    // drops `data` to undefined on key change, which collapses `meta.total` to 0
    // and makes the pagination control briefly render "5 / 1" mid-load.
    const query = useQuery({ queryFn, queryKey, retry: 0, placeholderData: keepPreviousData });
    return {
        ...query,
        isLoading: computed(() => query.isFetching.value || query.isLoading.value),
    };
}

export function useAudioFileIssueTypes() {
    return useQuery({
        queryKey: [...ROOT_KEY, 'issue-types'],
        queryFn: () =>
            http
                .get<{ data: IssueTypeOption[]; target_bitrate_kbps: number }>(
                    '/api/audio-file-issues/issue-types'
                )
                .then((response) => response.data),
        staleTime: 60 * 60 * 1000,
    });
}

export function useAudioFileIssuesForTrack(code: MaybeRefOrGetter<string | null | undefined>) {
    return useQuery({
        queryKey: computed(() => [...ROOT_KEY, 'by-track', toValue(code) ?? '']),
        enabled: computed(() => {
            const value = toValue(code);
            return typeof value === 'string' && value.trim() !== '';
        }),
        queryFn: () => {
            const value = (toValue(code) ?? '').trim();
            return http
                .get<AudioFileIssuesByTrackResponse>(
                    `/api/audio-file-issues/${encodeURIComponent(value.toUpperCase())}`
                )
                .then((response) => response.data);
        },
    });
}

export function useAudioFileIssueDdexMatches(code: MaybeRefOrGetter<string | null | undefined>) {
    return useQuery({
        queryKey: computed(() => [...ROOT_KEY, 'ddex-matches', toValue(code) ?? '']),
        enabled: computed(() => {
            const value = toValue(code);
            return typeof value === 'string' && value.trim() !== '';
        }),
        queryFn: () => {
            const value = (toValue(code) ?? '').trim().toUpperCase();
            return http
                .get<{ data: AudioFileIssueDdexMatchRow[] }>(
                    `/api/audio-file-issues/${encodeURIComponent(value)}/ddex-matches`
                )
                .then((response) => response.data.data);
        },
    });
}

/** Response from POST /audio-file-issues/{code}/ddex-matches/rerun */
export type RerunDdexMatchResponse = {
    message: string;
    meta: { async_operation_id: string; reused_in_flight_operation: boolean };
};

function parseRerunDdexMatchPayload(body: unknown): RerunDdexMatchResponse {
    let value: unknown = body;

    if (value === undefined || value === null || value === '') {
        throw new Error('Empty DDEX rerun response from server.');
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            throw new Error('Empty DDEX rerun response from server.');
        }
        try {
            value = JSON.parse(trimmed) as unknown;
        } catch {
            throw new Error('DDEX rerun response was not valid JSON.');
        }
    }

    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error('DDEX rerun response had an unexpected shape.');
    }

    let record = value as Record<string, unknown>;

    for (let depth = 0; depth < 8; depth += 1) {
        const meta = record.meta;
        if (typeof meta === 'object' && meta !== null && !Array.isArray(meta)) {
            const m = meta as Record<string, unknown>;
            let asyncOperationId: string | undefined;
            if (typeof m.async_operation_id === 'string' && m.async_operation_id !== '') {
                asyncOperationId = m.async_operation_id;
            } else if (typeof m.asyncOperationId === 'string' && m.asyncOperationId !== '') {
                asyncOperationId = m.asyncOperationId;
            }

            if (asyncOperationId !== undefined) {
                const message =
                    typeof record.message === 'string' && record.message.trim() !== ''
                        ? record.message
                        : 'DDEX matching has been queued.';

                const reusedMaybe = m.reused_in_flight_operation;
                const reused_in_flight_operation =
                    typeof reusedMaybe === 'boolean' ? reusedMaybe : false;

                return {
                    message,
                    meta: { async_operation_id: asyncOperationId, reused_in_flight_operation },
                };
            }
        }

        const next = record.data;
        if (typeof next === 'object' && next !== null && !Array.isArray(next)) {
            record = next as Record<string, unknown>;
        } else {
            break;
        }
    }

    throw new Error('Missing async_operation_id from DDEX rerun response.');
}

export async function postRerunAudioFileIssueDdexMatches(code: string): Promise<RerunDdexMatchResponse> {
    const normalized = encodeURIComponent(code.trim().toUpperCase());
    const response = await http.post<unknown>(`/api/audio-file-issues/${normalized}/ddex-matches/rerun`);

    return parseRerunDdexMatchPayload(response.data);
}

/** Queues backend raw_ddex rematch; refresh DDEX lists when Echo fires AsyncOperationFinished, or poll manually if offline. */
export function useRerunAudioFileIssueDdexMatches() {
    return useMutation({
        mutationFn: ({ code }: { code: string }) => postRerunAudioFileIssueDdexMatches(code),
    });
}


export async function fetchBackupPresignedDescriptor(code: string): Promise<AudioPlaybackDescriptorUrl> {
    const normalized = encodeURIComponent(code.toUpperCase());
    const { data } = await http.get<AudioPlaybackDescriptorUrl>(
        `/api/track-audio/live/${normalized}/backup`
    );

    return data;
}

export type BackupDownloadProgress = (loaded: number, total: number | null) => void;

/**
 * Download bytes from an absolute presigned URL (CloudFront/S3). Progress uses xhr `loaded` and
 * `total` when provided, otherwise `contentLengthFromApi` as the denominator when known.
 */
export async function downloadPresignedUrlAsBlob(
    absoluteUrl: string,
    contentLengthFromApi: number | null,
    onProgress: BackupDownloadProgress,
    // Optional, and only the player's prefetch passes it. Aborting is a
    // BANDWIDTH measure, not a correctness one: it cannot un-resolve a response
    // that already landed, so the player still checks its own generation token
    // after every await. Existing callers compile untouched.
    signal?: AbortSignal
): Promise<Blob> {
    const client = axios.create();
    const response = await client.get<Blob>(absoluteUrl, {
        responseType: 'blob',
        signal,
        onDownloadProgress: (progressEvent) => {
            const loaded = progressEvent.loaded;
            let total: number | null = null;
            if (progressEvent.total != null && progressEvent.total > 0) {
                total = progressEvent.total;
            } else if (contentLengthFromApi != null && contentLengthFromApi > 0) {
                total = contentLengthFromApi;
            }
            onProgress(loaded, total);
        },
    });

    return response.data;
}

export async function fetchPlaybackDescriptor(
    code: string,
    // Live-track playback only. The original-bitrate backup has its own
    // AFI-page-gated endpoint — use fetchBackupPresignedDescriptor for it.
    source: 'live_mp3' | 'live_hls'
): Promise<AudioPlaybackDescriptor> {
    const normalized = encodeURIComponent(code.toUpperCase());
    const { data } = await http.get<AudioPlaybackDescriptor>(
        `/api/track-audio/live/${normalized}/descriptor`,
        {
            params: { source },
        }
    );

    return data;
}

export type RerunArgs = { code: string };

/** Response from POST /audio-file-issues/{code}/rerun (queued deep re-verify). */
export type RerunVerificationResponse = {
    message: string;
    meta: { async_operation_id: string; reused_in_flight_operation: boolean };
};

function parseRerunVerificationPayload(body: unknown): RerunVerificationResponse {
    let value: unknown = body;

    if (value === undefined || value === null || value === '') {
        throw new Error('Empty re-verify response from server.');
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            throw new Error('Empty re-verify response from server.');
        }
        try {
            value = JSON.parse(trimmed) as unknown;
        } catch {
            throw new Error('Re-verify response was not valid JSON.');
        }
    }

    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error('Re-verify response had an unexpected shape.');
    }

    let record = value as Record<string, unknown>;

    for (let depth = 0; depth < 8; depth += 1) {
        const meta = record.meta;
        if (typeof meta === 'object' && meta !== null && !Array.isArray(meta)) {
            const m = meta as Record<string, unknown>;
            let asyncOperationId: string | undefined;
            if (typeof m.async_operation_id === 'string' && m.async_operation_id !== '') {
                asyncOperationId = m.async_operation_id;
            } else if (typeof m.asyncOperationId === 'string' && m.asyncOperationId !== '') {
                asyncOperationId = m.asyncOperationId;
            }

            if (asyncOperationId !== undefined) {
                const message =
                    typeof record.message === 'string' && record.message.trim() !== ''
                        ? record.message
                        : 'Re-verification has been queued.';

                const reusedMaybe = m.reused_in_flight_operation;
                const reused_in_flight_operation =
                    typeof reusedMaybe === 'boolean' ? reusedMaybe : false;

                return {
                    message,
                    meta: { async_operation_id: asyncOperationId, reused_in_flight_operation },
                };
            }
        }

        const next = record.data;
        if (typeof next === 'object' && next !== null && !Array.isArray(next)) {
            record = next as Record<string, unknown>;
        } else {
            break;
        }
    }

    throw new Error('Missing async_operation_id from re-verify response.');
}

export async function postRerunAudioFileVerification(
    code: string
): Promise<RerunVerificationResponse> {
    const normalized = encodeURIComponent(code.trim().toUpperCase());
    const response = await http.post<unknown>(`/api/audio-file-issues/${normalized}/rerun`);

    return parseRerunVerificationPayload(response.data);
}

/**
 * Queues a backend deep re-verify. The verify is too slow to run in-request
 * (S3 GET + AES decrypt + frame walk + mp3val), so the SPA subscribes to the
 * AsyncOperationFinished channel for the returned id and refreshes the track's
 * issues when it fires (polling fallback via GET /api/async-operations/pending).
 */
export function useRerunAudioFileIssues() {
    return useMutation({
        mutationFn: ({ code }: RerunArgs) => postRerunAudioFileVerification(code),
    });
}

export type PresignFixResponse = {
    url: string;
    key: string;
    bucket: string;
    content_type: string;
    max_bytes: number;
    expires_in_seconds: number;
};

export function useFixUploadPresign() {
    return useMutation({
        mutationFn: ({ code }: { code: string }) =>
            http
                .post<PresignFixResponse>(
                    `/api/audio-file-issues/${encodeURIComponent(code.toUpperCase())}/fix/presign`
                )
                .then((r) => r.data),
    });
}

export function useApplyAudioFix() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            code,
            source,
            s3_key,
        }: {
            code: string;
            source: 'uploaded' | 'backup';
            s3_key?: string;
        }) =>
            http
                .post<{ live_track_action_id: number }>(
                    `/api/audio-file-issues/${encodeURIComponent(code.toUpperCase())}/fix/apply`,
                    { source, s3_key }
                )
                .then((r) => r.data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'list'] });
            queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'by-track', variables.code.toUpperCase()],
            });
        },
    });
}

export function useLinkRawDdexToLiveTrack() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            liveTrackId,
            rawDdexTrackId,
        }: {
            code: string;
            liveTrackId: number;
            rawDdexTrackId: number;
        }) =>
            http
                .patch(`/api/live_track/${liveTrackId}`, { raw_ddex_track_id: rawDdexTrackId })
                .then((r) => r.data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'list'] });
            queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'by-track', variables.code.toUpperCase()],
            });
            queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'ddex-matches', variables.code.toUpperCase()],
            });
        },
    });
}
